// ===========================================
// Setup Guide Content
// ===========================================
// Step-by-step instructions for setting up third-party integrations

import type { SetupStep } from './SetupGuideCard';

// ===========================================
// STRIPE
// ===========================================

export const stripeSetupSteps: SetupStep[] = [
  {
    title: 'Log in to your Stripe Dashboard',
    description: 'If you don\'t have a Stripe account yet, sign up at stripe.com',
    link: {
      url: 'https://dashboard.stripe.com',
      label: 'Go to Stripe Dashboard',
    },
  },
  {
    title: 'Navigate to API keys',
    description: 'In the left sidebar, go to Developers → API keys',
    substeps: [
      'Click on "Developers" in the left menu',
      'Select "API keys" from the submenu',
    ],
  },
  {
    title: 'Copy your Secret Key',
    description: 'Find the "Secret key" section and reveal the key',
    code: 'sk_live_... or sk_test_...',
    warning: 'Keep this secret! Never share it publicly or commit it to version control.',
  },
  {
    title: 'Copy your Publishable Key',
    description: 'Find the "Publishable key" section',
    code: 'pk_live_... or pk_test_...',
  },
  {
    // ⚠️ Keep this list in step with the events the backend handles —
    // apps/backend/tests/unit/services/stripeSetupGuideEvents.test.ts fails if a
    // handled event is missing here or a listed one is not handled.
    title: 'Set up the webhook (required for online payments)',
    description:
      'The webhook is how Stripe tells us a payment happened. It records invoice payments even if a parent closes the page before returning, issues paid event tickets, keeps subscriptions in sync, and unlocks an invoice for amending when a family leaves the payment page.',
    substeps: [
      'Go to Developers → Webhooks',
      'If an endpoint for the URL below already exists, EDIT it and add any missing events. Do not create a second one: a new endpoint gets a new signing secret.',
      'Otherwise click "Add endpoint" and paste the webhook URL provided below',
      'Select these events to listen for:',
      'Online payments (invoices, event tickets, registrations):',
      '  • checkout.session.completed',
      '  • checkout.session.expired',
      'Refunds (a refunded registration fee is flagged for the office):',
      '  • charge.refunded',
      'Subscriptions:',
      '  • invoice.payment_succeeded',
      '  • invoice.payment_failed',
      '  • invoice.upcoming',
      '  • invoice.created',
      '  • customer.subscription.created',
      '  • customer.subscription.updated',
      '  • customer.subscription.deleted',
      '  • customer.subscription.paused',
      '  • customer.subscription.resumed',
      '  • customer.subscription.trial_will_end',
      'Save the endpoint, then copy its "Signing secret" (starts with whsec_)',
    ],
    warning:
      'Test mode and live mode have separate webhook endpoints and separate signing secrets. Set up the endpoint in the same mode as the keys you enter above (sk_test_/pk_test_ keys → test mode; sk_live_/pk_live_ → live mode).',
    link: {
      url: 'https://dashboard.stripe.com/webhooks',
      label: 'Configure Webhooks',
    },
  },
  {
    title: 'Paste the keys into the form above',
    description:
      'Enter all three keys (Secret Key, Publishable Key and Webhook Secret) in the API Keys section above, then click "Verify" to test the connection. Without the Webhook Secret, Stripe\'s payment notifications cannot be verified: paid event tickets are not issued, and an invoice paid by a parent who closes the page before returning stays unpaid.',
  },
];

// ===========================================
// TWILIO
// ===========================================

export const twilioSetupSteps: SetupStep[] = [
  {
    title: 'Log in to Twilio Console',
    description: 'If you don\'t have a Twilio account, sign up at twilio.com',
    link: {
      url: 'https://console.twilio.com',
      label: 'Go to Twilio Console',
    },
  },
  {
    title: 'Find your Account SID and Auth Token',
    description: 'These are displayed on your console dashboard',
    substeps: [
      'Look for the "Account Info" section on your dashboard',
      'Copy your "Account SID" (starts with AC)',
      'Click "View" next to Auth Token and copy it',
    ],
    code: 'Account SID: AC...',
  },
  {
    title: 'Get a phone number for SMS (Optional)',
    description: 'Required if you want to send SMS messages',
    substeps: [
      'Go to Phone Numbers → Manage → Buy a number',
      'Choose a number with SMS capability',
      'Purchase the number',
      'Copy the phone number in E.164 format (e.g., +61412345678)',
    ],
    link: {
      url: 'https://console.twilio.com/us1/develop/phone-numbers/manage/incoming',
      label: 'Get a Phone Number',
    },
  },
  {
    title: 'Set up WhatsApp (Optional)',
    description: 'Required if you want to send WhatsApp messages',
    substeps: [
      'Go to Messaging → Try it out → Send a WhatsApp message',
      'Follow the setup wizard to connect your WhatsApp Business Account',
      'Once approved, copy your WhatsApp sender number (format: whatsapp:+61...)',
    ],
    warning: 'WhatsApp Business requires approval from Meta. The sandbox can be used for testing.',
    link: {
      url: 'https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn',
      label: 'Set up WhatsApp',
    },
  },
  {
    title: 'Paste the credentials into the form above',
    description: 'Enter your Account SID, Auth Token, and optionally your phone numbers in the API Keys section above. Click "Verify" to test the connection.',
  },
];

// ===========================================
// MAILGUN
// ===========================================

export const mailgunSetupSteps: SetupStep[] = [
  {
    title: 'Log in to Mailgun',
    description: 'If you don\'t have a Mailgun account, sign up at mailgun.com',
    link: {
      url: 'https://app.mailgun.com',
      label: 'Go to Mailgun',
    },
  },
  {
    title: 'Navigate to API Keys',
    description: 'Find your API credentials',
    substeps: [
      'Click on "Settings" in the left sidebar',
      'Select "API Keys"',
      'Copy your "Private API key"',
    ],
    code: 'key-...',
    warning: 'Keep this key secret! Never expose it in client-side code.',
  },
  {
    title: 'Add and verify your domain',
    description: 'You need a verified domain to send emails',
    substeps: [
      'Go to Sending → Domains',
      'Click "Add New Domain"',
      'Enter your domain (e.g., mg.musicnme.com.au)',
      'Follow the DNS verification steps',
      'Wait for verification (can take up to 48 hours)',
    ],
    link: {
      url: 'https://app.mailgun.com/app/sending/domains',
      label: 'Manage Domains',
    },
  },
  {
    title: 'Configure your sending domain',
    description: 'Copy your verified domain name',
    code: 'mg.example.com',
  },
  {
    title: 'Set your From Email (Optional)',
    description: 'The email address that will appear in the "From" field',
    code: 'noreply@example.com',
    substeps: [
      'Use a subdomain you own and have verified',
      'Common choices: noreply@, hello@, notifications@',
    ],
  },
  {
    title: 'Paste the credentials into the form above',
    description: 'Enter your API Key, Domain, and optionally From Email in the API Keys section above. Click "Verify" to test the connection.',
  },
];

// ===========================================
// SLACK
// ===========================================

export const slackSetupSteps: SetupStep[] = [
  {
    title: 'Create a Slack App',
    description: 'You need to create a Slack app to connect your workspace',
    substeps: [
      'Go to api.slack.com/apps',
      'Click "Create New App"',
      'Choose "From scratch"',
      'Name your app (e.g., "Music \'n Me Notifications")',
      'Select your workspace',
      'Click "Create App"',
    ],
    link: {
      url: 'https://api.slack.com/apps',
      label: 'Go to Slack Apps',
    },
  },
  {
    title: 'Get your App Credentials',
    description: 'Find your Client ID and Client Secret',
    substeps: [
      'In your app settings, go to "Basic Information"',
      'Scroll to "App Credentials"',
      'Copy your "Client ID"',
      'Click "Show" next to Client Secret and copy it',
    ],
    code: 'Client ID: 1234567890.1234567890',
    warning: 'Keep your Client Secret confidential. Never expose it publicly.',
  },
  {
    title: 'Get your Signing Secret',
    description: 'This verifies requests from Slack',
    substeps: [
      'In "Basic Information", find "App Credentials"',
      'Copy the "Signing Secret"',
    ],
  },
  {
    title: 'Configure OAuth & Permissions',
    description: 'Set up the required scopes for your app',
    substeps: [
      'Go to "OAuth & Permissions" in the sidebar',
      'Scroll to "Scopes" → "Bot Token Scopes"',
      'Add these scopes: chat:write, channels:read, users:read, users:read.email',
    ],
    link: {
      url: 'https://api.slack.com/scopes',
      label: 'Learn about OAuth scopes',
    },
  },
  {
    title: 'Set Redirect URL',
    description: 'Add the OAuth redirect URL to your app',
    substeps: [
      'Still in "OAuth & Permissions"',
      'Under "Redirect URLs", click "Add New Redirect URL"',
      'Paste the redirect URL: [Your App URL]/api/integrations/slack/callback',
      'Click "Add" then "Save URLs"',
    ],
  },
  {
    title: 'Paste the credentials into the form above',
    description: 'Enter your Client ID, Client Secret, and Signing Secret in the API Keys section above. Click "Verify" to test, then use "Connect to Slack" to authorize.',
  },
];

// ===========================================
// WEBHOOK URLS
// ===========================================

export const getWebhookUrl = (
  integration: 'stripe' | 'twilio' | 'mailgun' | 'slack',
  schoolSlug?: string
): string => {
  const baseUrl = window.location.origin;

  if (integration === 'stripe') {
    if (!schoolSlug) {
      return `${baseUrl}/api/v1/webhooks/stripe/<your-school-slug>`;
    }
    return `${baseUrl}/api/v1/webhooks/stripe/${schoolSlug}`;
  }

  const webhookPaths = {
    twilio: '/api/webhooks/twilio',
    mailgun: '/api/webhooks/mailgun',
    slack: '/api/integrations/slack/callback',
  };

  return `${baseUrl}${webhookPaths[integration]}`;
};

// ===========================================
// DOCS LINKS
// ===========================================

export const docsLinks = {
  stripe: 'https://stripe.com/docs/api',
  twilio: 'https://www.twilio.com/docs',
  mailgun: 'https://documentation.mailgun.com/en/latest/',
  slack: 'https://api.slack.com/docs',
};
